import { makeStyles } from "tss-react/mui";
import {
  MiBox,
  MiButton,
  MiChip,
  MiIcon,
  MiSectionHeader,
} from "@miplatform/components";
import {
  BUTTON_COLORS,
  BUTTON_VARIANTS,
  TYPOGRAPHY_VARIANTS,
} from "@miplatform/constants";
import { simpleSort, toUpperCase } from "@miplatform/utils";
import { mdiLockOutline, mdiWall } from "@mdi/js";
import { Box, Typography } from "@mui/material";

export function OptionsList({
  planDetails,
  handleOptionClick,
  handleReviewed,
  handleRemoveOrReactivateOption,
}) {
  const { classes, theme } = useStyles();

  const getOptionBorderStyle = (option) => {
    if (option.optionRemoved) {
      return `2px solid ${theme.palette.red.main}`;
    }
    if (option.reviewed) {
      return `2px solid ${theme.palette.orange.main}`;
    }
    return `1px solid ${theme.palette.grey.accent}`;
  };

  return (
    <MiBox className={classes.container}>
      <MiSectionHeader title="Options" />
      <Box className={classes.overflowContainer}>
        {planDetails?.options
          ?.sort((a, b) => simpleSort(a.name, b.name))
          ?.map((option, index) => (
            <Box
              key={option.optionId}
              className={classes.optionCard}
              sx={{
                backgroundColor:
                  option.optionRemoved && theme.palette.red.light,
                border: getOptionBorderStyle(option),
              }}
            >
              <Box className={classes.optionHeader}>
                <Box className={classes.optionStatusRow}>
                  {option.reviewed && !option.optionRemoved && (
                    <MiChip
                      label="Approved"
                      size="small"
                      className={classes.approvedStatusChip}
                    />
                  )}
                  {option.optionRemoved && (
                    <MiChip
                      label="Removed"
                      size="small"
                      className={classes.removedStatusChip}
                    />
                  )}
                  {!option.optionRemoved && !option.reviewed && (
                    <MiIcon
                      path={mdiLockOutline}
                      size={0.8}
                      color={theme.palette.red.main}
                    />
                  )}
                  <Typography
                    className={classes.optionNumber}
                    sx={
                      option.optionRemoved && {
                        color: theme.palette.red.main,
                        textDecoration: "line-through",
                      }
                    }
                  >
                    Option {index + 1}
                  </Typography>
                </Box>
                <Box className={classes.optionActions}>
                  {(option.optionRemoved || !option.reviewed) && (
                    <MiButton
                      title={
                        option.optionRemoved
                          ? "Reactivate Option"
                          : "Remove Option"
                      }
                      variant={BUTTON_VARIANTS.TEXT}
                      className={classes.underlinedButton}
                      onClick={() => handleRemoveOrReactivateOption(option)}
                    />
                  )}
                  {!option.optionRemoved && !option.reviewed && (
                    <>
                      <MiButton
                        title="Modify Option"
                        variant={BUTTON_VARIANTS.TEXT}
                        className={classes.underlinedButton}
                        color={BUTTON_COLORS.BLACK}
                        onClick={() => {
                          handleOptionClick(option);
                        }}
                      />
                      <MiButton
                        title="Reviewed"
                        variant={BUTTON_VARIANTS.CONTAINED}
                        onClick={() => {
                          handleReviewed(option.optionId, true);
                        }}
                      />
                    </>
                  )}
                  {!option.optionRemoved && option.reviewed && (
                    <MiButton
                      title="Undo Review"
                      variant={BUTTON_VARIANTS.CONTAINED}
                      onClick={() => {
                        handleReviewed(option.optionId, false);
                      }}
                    />
                  )}
                </Box>
              </Box>
              <Box display="flex" flex={1} alignItems="center">
                <Box className={classes.optionIcon}>
                  <Typography fontSize={theme.fontSize.small}>
                    STRUCTURAL
                  </Typography>
                  <MiIcon path={mdiWall} size={0.8} />
                  <Typography fontSize={theme.fontSize.small}>
                    OPTION
                  </Typography>
                </Box>
                <Box className={classes.optionContent}>
                  <Box className={classes.optionTitle}>
                    <Typography
                      fontWeight={theme.fontWeight.semiBold}
                      variant={TYPOGRAPHY_VARIANTS.BODY2}
                    >
                      {toUpperCase(option?.name)}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          ))}
      </Box>
    </MiBox>
  );
}

const useStyles = makeStyles()((theme) => ({
  container: {
    margin: 0,
    display: "flex",
    flexDirection: "column",
    flex: 1,
  },
  optionActions: {
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap",
  },
  optionNumber: {
    fontWeight: theme.fontWeight.semiBold,
    fontSize: 14,
  },
  optionStatusRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  optionHeader: {
    display: "flex",
    justifyContent: "space-between",
    width: "100%",
  },
  optionCard: {
    display: "flex",
    flexDirection: "column",
    alignContent: "center",
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.5),
    gap: theme.spacing(2),
    borderRadius: 8,
    boxShadow: `1px 1px 1px ${theme.palette.grey.accent}`,
    "&:hover": {
      boxShadow: `1px 1px 3px ${theme.palette.grey.accent}`,
    },
    transition: "box-shadow 0.2s ease-in-out",
  },
  optionIcon: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: 58,
    minWidth: 80,
    borderRadius: 8,
    backgroundColor: theme.palette.secondary.light,
    color: theme.palette.secondary.main,
    padding: theme.spacing(0, 2),
  },
  optionContent: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    flex: 1,
    margin: theme.spacing(0, 2),
  },
  optionTitle: {
    display: "flex",
    gap: theme.spacing(3),
    paddingBottom: theme.spacing(1),
  },
  removedStatusChip: {
    border: `1px solid ${theme.palette.red.main}`,
    backgroundColor: theme.palette.red.light,
    borderRadius: 4,
    height: 20,
    color: theme.palette.red.main,
  },
  approvedStatusChip: {
    border: `1px solid ${theme.palette.green.main}`,
    backgroundColor: theme.palette.green.light,
    borderRadius: 4,
    height: 20,
    color: theme.palette.green.main,
  },
  underlinedButton: {
    textDecoration: "underline",
  },
  overflowContainer: {
    overflow: "auto",
    height: "100%",
  },
}));
